import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders } from "../lib/supabase";
import { useWallet } from "../hooks/useWallet";
import { useCurrency } from "../context/CurrencyContext";
import { PRICING_CONFIG, FAIR_USE_PER_MONTH, tierPrice } from "../lib/pricingConfig";
import { CurrencyPill } from "./CurrencyPill";

export const PACKS = PRICING_CONFIG;
export const creditsLabel = (t) => (t.credits === "unlimited" ? "Unlimited*" : `${t.credits} credits`);

export const startCheckout = async (id, currency) => {
  const r = await fetch(`${API}/billing/checkout`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify({ pack_id: id, currency, origin_url: window.location.origin }) });
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail === "WALLET_READ_ONLY" ? "Your account is read-only after a refund." : d.detail || "Checkout failed");
  window.location.href = d.checkout_url;
};

export const PackGrid = ({ testidPrefix = "buy" }) => {
  const { wallet } = useWallet();
  const { currency } = useCurrency();
  const [busy, setBusy] = useState("");
  const [agreed, setAgreed] = useState(false);
  const readOnly = wallet?.subscription_status === "refunded_read_only";
  const subscribed = wallet?.subscription_status === "active" || wallet?.subscription_status === "past_due";
  const [hl, setHl] = useState(false);
  const nudge = () => { const el = document.querySelector('[data-testid="non-refundable-checkbox"]'); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus(); setHl(true); setTimeout(() => setHl(false), 1600); toast.message("Tick the acknowledgement to continue"); };
  const buy = async (id) => {
    if (!agreed) return nudge();
    setBusy(id);
    try { await startCheckout(id, currency); } catch (e) { toast.error(e.message); setBusy(""); }
  };
  return (
    <div data-testid="pack-grid" data-currency={currency}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted">Prices in <span className="notranslate" translate="no">{currency}</span></span>
        <CurrencyPill testid={`${testidPrefix}-currency-pill`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        {PACKS.map((p) => {
          const hot = p.type === "subscription";
          const disabled = !!busy || readOnly || (hot && subscribed);
          return (
            <button key={p.id} type="button" className={`pack ${hot ? "pack-hot" : ""} ${!agreed || disabled ? "pack-locked" : ""}`} onClick={() => buy(p.id)} disabled={disabled} aria-disabled={!agreed || disabled ? "true" : undefined} data-testid={`${testidPrefix}-${p.id}`} data-locked={!agreed}>
              {hot && <Zap size={14} className="absolute top-2 right-2" />}
              <span className="text-xs text-muted">{p.name}</span>
              <span className="font-bold">{creditsLabel(p)}{p.fairUsePerMonth && <span className="block text-[10px] text-muted font-normal">fair use {FAIR_USE_PER_MONTH}/mo</span>}</span>
              <span className="text-lg font-black">{busy === p.id ? "…" : hot && subscribed ? "Active" : <span className="notranslate" translate="no" data-testid={`${testidPrefix}-${p.id}-price`}>{tierPrice(p, currency)}</span>}</span>
            </button>
          );
        })}
      </div>
      <label className={`agree-row mt-4 ${readOnly ? "opacity-50" : ""} ${hl ? "agree-highlight" : ""}`} data-testid="non-refundable-row">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} disabled={readOnly} data-testid="non-refundable-checkbox" />
        <span className="text-sm">I understand credits and subscriptions are non-refundable except where required by law. <Link to="/terms" className="underline" data-testid="non-refundable-terms-link">Terms</Link></span>
      </label>
      {readOnly && <p className="text-xs text-[#FF7A7A] mt-2" data-testid="pack-grid-readonly">Purchases are disabled on a refunded account.</p>}
    </div>
  );
};
